import { EditNewIcon } from '@app/components/icons/edit-new';
import { Spinner } from '@app/components/icons/spinner';
import { Tooltip } from '@app/components/tooltip';
import { FLAG_CONTENT_HUB_ENABLE_SPEAKER_EDIT } from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import { Profile } from '@app/routes/content/routes/recordings/components/icons';
import { TranscriptSegmentGroup } from '@app/routes/content/routes/recordings/domain';
import {
  selectRecordingTranscriptSpeakers,
  selectSpeakerManagement,
} from '@app/routes/content/routes/recordings/selector';
import {
  calculateTextContainerHeight,
  getSpeakerDisplayName,
} from '@app/routes/content/routes/recordings/utils';
import { formatDuration } from '@app/routes/content/utils';
import { getContentHubMediaHost } from '@app/utilities/utils';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useRecordingAnalytics } from '../../../hooks';
import { useVideoPlayer } from '../../video-player-context';

export const SegmentGroup = ({
  group,
  index,
  activeSegmentIndex = -1,
  searchQuery = '',
}: {
  group: TranscriptSegmentGroup;
  index: number;
  activeSegmentIndex?: number;
  searchQuery?: string;
}) => {
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { speaker, segments } = group;
  const { seek } = useVideoPlayer();
  const dispatch = useDispatch();
  const flags = useFlags();
  const speakersMapping = useSelector(selectRecordingTranscriptSpeakers);
  const speakerManagement = useSelector(selectSpeakerManagement);
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();

  const isSpeakerEditEnabled = flags?.[FLAG_CONTENT_HUB_ENABLE_SPEAKER_EDIT];

  // Get the display name for the speaker using the mapping
  const displaySpeakerName = getSpeakerDisplayName(speaker, speakersMapping);

  // Get the speaker data and image URL directly from the mapping
  const speakerData = speakersMapping?.[speaker];
  const speakerImageUrl = speakerData?.imageUrl || null;

  const handleGroupClick = () => {
    trackRecordingEvent('ch_recording_editor_transcript_group_clicked');
    seek(group.start);
  };

  const handleSegmentClick = (segmentStart: number) => {
    trackRecordingEvent('ch_recording_editor_transcript_segment_clicked');
    seek(segmentStart);
  };

  const handleGroupKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      seek(group.start);
    }
  };

  const handleSegmentKeyDown = (
    event: React.KeyboardEvent,
    segmentStart: number,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      seek(segmentStart);
    }
  };

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch({
      type: '[recording] select speaker clicked',
      payload: {
        speakerKey: speaker,
        currentSpeakerName: displaySpeakerName,
      },
    });
  };

  const handleEditKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      dispatch({
        type: '[recording] select speaker clicked',
        payload: {
          speakerKey: speaker,
          currentSpeakerName: displaySpeakerName,
        },
      });
    }
  };
  return (
    <div>
      <div
        className="flex items-start gap-2 p-2 cursor-pointer hover:bg-spring-neutral-b0/5 rounded-lg transition-colors duration-200"
        key={`${searchQuery}-group-${index}`}
        data-group-index={index}
        onClick={handleGroupClick}
        onKeyDown={handleGroupKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Seek to ${formatDuration(group.start)}`}
        data-testid="transcript-segment-group"
      >
        {isSpeakerEditEnabled ? (
          <Tooltip tooltip={t('content-hub.edit')}>
            <div
              className="w-8 h-8 rounded-full bg-spring-neutral-b4 flex items-center justify-center text-white shrink-0 relative group cursor-pointer"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={handleEditClick}
              onKeyDown={handleEditKeyDown}
              tabIndex={0}
              role="button"
              aria-label="Edit speaker"
            >
              {speakerManagement.status === 'processing' &&
              speakerManagement.speakerKey === speaker ? (
                <Spinner className="w-4 h-4 text-white" />
              ) : isHovered ? (
                <EditNewIcon className="w-4 h-4 text-white" />
              ) : speakerImageUrl ? (
                <div
                  className="absolute inset-0 bg-center bg-cover bg-no-repeat rounded-full"
                  style={{
                    backgroundImage: `url('${
                      speakerImageUrl.startsWith('https')
                        ? speakerImageUrl
                        : `${getContentHubMediaHost()}${speakerImageUrl}`
                    }')`,
                  }}
                />
              ) : (
                <Profile className="w-5 h-5" />
              )}
            </div>
          </Tooltip>
        ) : (
          <div className="w-8 h-8 rounded-full bg-spring-neutral-b4 flex items-center justify-center text-white shrink-0 relative">
            {speakerImageUrl ? (
              <div
                className="absolute inset-0 bg-center bg-cover bg-no-repeat rounded-full"
                style={{
                  backgroundImage: `url('${
                    speakerImageUrl.startsWith('https')
                      ? speakerImageUrl
                      : `${getContentHubMediaHost()}${speakerImageUrl}`
                  }')`,
                }}
              />
            ) : (
              <Profile className="w-5 h-5" />
            )}
          </div>
        )}
        <div className="flex flex-col gap-1.5 flex-1 pt-0.5">
          <div className="flex items-center justify-start gap-1">
            <span className="text-sm font-medium">
              {displaySpeakerName || ''}
            </span>
            <div className="w-0.5 h-0.5 rounded-full bg-spring-neutral-b1/35 shrink-0" />
            <span className="text-xs text-spring-neutral-b1/35 shrink-0">
              {formatDuration(group.start)}
            </span>
          </div>
          {segments.map((segment: any, segmentIndex: number) => (
            <div
              key={`group-${index}-segment-${segmentIndex}`}
              className={cx(
                'text-sm p-0 m-0 font-normal !leading-4 cursor-pointer hover:bg-spring-neutral-b0/5 rounded px-1 py-0.5 -mx-1 -my-0.5 transition-colors duration-200',
                {
                  'text-spring-cobranding': segmentIndex === activeSegmentIndex,
                },
              )}
              data-segment-index={segmentIndex}
              onClick={e => {
                e.stopPropagation();
                handleSegmentClick(segment.start);
              }}
              onKeyDown={e => {
                e.stopPropagation();
                handleSegmentKeyDown(e, segment.start);
              }}
              tabIndex={0}
              role="button"
              aria-label={`Seek to segment at ${formatDuration(segment.start)}`}
              data-testid="transcript-segment"
            >
              {segment.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CONTAINER_PADDING = 16;
const CONTAINER_GAP = 8;
const PROFILE_ICON_WIDTH = 32;
const SPEAKER_NAME_HEIGHT = 20;
const GAP_BETWEEN_SEGMENTS = 6;
const MEASUREMENT_OPTIONS = {
  fontSize: 14,
  fontFamily: 'Inter',
  fontWeight: 'normal',
  lineHeight: 16,
};

export const getSegmentGroupSize = (
  group: TranscriptSegmentGroup,
  containerWidth: number,
) => {
  const { segments } = group;
  let currentHeight = CONTAINER_PADDING + SPEAKER_NAME_HEIGHT;

  const segmentContainerWidth =
    containerWidth - CONTAINER_PADDING * 4 - CONTAINER_GAP - PROFILE_ICON_WIDTH;

  segments.forEach(segment => {
    const textHeight = calculateTextContainerHeight(
      segment.text,
      segmentContainerWidth,
      MEASUREMENT_OPTIONS,
    );
    currentHeight += GAP_BETWEEN_SEGMENTS + textHeight;
  });

  return currentHeight;
};
